import { useEffect, useState } from 'react'
import { add } from 'vend-number'
import {
  useCashMovementsManager,
  useRegisterClosureManager,
  useRegisterManager,
} from '../../ng-compat/hooks'
import { RegisterClosure } from '../../util/register-closure-types'
import { CASH_MOVEMENT_TYPES } from '../../constants'

export type RegisterClosureSummaryType = 'last' | 'current'

interface UseRegisterClosureParams {
  type: RegisterClosureSummaryType
}

export const getOpeningFloat = (closure: RegisterClosure | null) => {
  if (!closure || !closure.cashMovements) {
    return
  }

  const floatMovement = closure.cashMovements.find(
    movement => movement.type === CASH_MOVEMENT_TYPES.OPENING_CASH_FLOAT
  )

  if (!floatMovement) {
    return
  }

  return floatMovement.amount
}

export const getCashMovementsTotal = (closure: RegisterClosure | null) => {
  if (!closure || !closure.cashMovements) {
    return 0
  }

  return closure.cashMovements.reduce((total, movement) => {
    return add(total, movement.amount)
  }, 0)
}

export const useRegisterClosure = ({ type }: UseRegisterClosureParams) => {
  const registerManager = useRegisterManager()
  const cashMovementsManager = useCashMovementsManager()
  const registerClosureManager = useRegisterClosureManager()
  const [closure, setClosure] = useState<RegisterClosure | null>(null)

  useEffect(() => {
    const fetchClosure = async () => {
      if (type === 'last') {
        const closureData = await registerClosureManager.getLastRegisterClosureSummary()
        setClosure(closureData)
        return
      }

      const closureData = await registerClosureManager.getRegisterClosureSummary()

      if (cashMovementsManager.isCashManagementEnabled()) {
        const openingFloat = getOpeningFloat(closureData)
        setClosure({
          ...closureData,
          openingCashFloat: openingFloat || 0,
        })
        return
      }
      setClosure(closureData)
    }

    fetchClosure()

    registerManager.on('registerIdChange', fetchClosure)

    return () => {
      registerManager.off('registerIdChange', fetchClosure)
    }
  }, [cashMovementsManager, registerClosureManager, registerManager, type])

  return closure
}
